import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import PageTitle from "../../common/PageTitle";
import {
  apiGetReleaseDetails,
  apiListAllBasesWithStats,
  apiReleaseBase,
  apiDeleteRelease,
} from "../../../api/api";
import { useToast } from "../../../context/ToastContext";
import "./SettingsBases.css";

const isBaseReleased = (value) => value === true || value === 1 || value === "1";

const SettingsBases = () => {
  const { addToast } = useToast();

  const [data, setData] = useState([]);
  const [totalBases, setTotalBases] = useState(0);
  const [totalReleases, setTotalReleases] = useState(0);
  const [totalReleased, setTotalReleased] = useState(0);
  const [totalBlocked, setTotalBlocked] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filterBaseName, setFilterBaseName] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const updateCounters = useCallback((items) => {
    let basesCounter = 0;
    let lastBase = "";
    let releasedCounter = 0;
    let blockedCounter = 0;

    items.forEach((release) => {
      if (release.baseName !== lastBase) {
        basesCounter += 1;
        lastBase = release.baseName;
      }

      if (isBaseReleased(release.isReleased)) {
        releasedCounter += 1;
      } else {
        blockedCounter += 1;
      }
    });

    setTotalBases(basesCounter);
    setTotalReleases(items.length);
    setTotalReleased(releasedCounter);
    setTotalBlocked(blockedCounter);
  }, []);

  const refreshBases = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const response = await apiListAllBasesWithStats();
      if (!response) {
        addToast("Erro ao carregar bases");
        return;
      }
      setData(response);
      updateCounters(response);
    } catch (error) {
      console.error("Erro ao carregar bases:", error);
    } finally {
      setIsRefreshing(false);
    }
  }, [addToast, updateCounters]);

  useEffect(() => {
    refreshBases();
  }, [refreshBases]);

  const handleDetails = async (item) => {
    setSelectedItem(item);
    setShowModal(true);
    setLoadingDetails(true);
    setItemDetails(null);

    try {
      const response = await apiGetReleaseDetails(item.releaseId);
      if (!response) {
        addToast("Erro ao carregar detalhes da base");
        return;
      } else {
        setItemDetails(Array.isArray(response) ? response[0] : response);
      }
    } catch (error) {
      addToast(`Erro ao carregar detalhes da base: ${error}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setItemDetails(null);
  };

  const handleRelease = async (item) => {
    const operation = isBaseReleased(item.isReleased) ? "bloqueada" : "liberada";

    try {
      const response = await apiReleaseBase(item.releaseId, item.isReleased);
      if (!response) {
        addToast(`Erro ao ${operation} base`);
        return;
      }
      if (response.status === 200) {
        addToast(`Base ${operation} com sucesso!`);

        const updatedData = data.map((baseItem) => {
          if (baseItem.releaseId === item.releaseId) {
            return { ...baseItem, isReleased: isBaseReleased(baseItem.isReleased) ? 0 : 1 };
          }
          return baseItem;
        });

        setData(updatedData);
        updateCounters(updatedData);
      } else {
        addToast(`Erro ao ${operation} base!`);
      }
    } catch (error) {
      addToast(`Erro ao ${operation} base: ${error}`);
    }
  };

  const handleOpenDeleteModal = (item) => {
    setItemToDelete(item);
    setDeleteText("");
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteText("");
  };

  const handleDeleteBase = async () => {
    if (!itemToDelete) {
      return;
    }

    try {
      setIsDeleting(true);

      const response = await apiDeleteRelease(itemToDelete.releaseId);
      if (!response) {
        addToast("Erro ao excluir base");
        return;
      }

      if (response.status === 200) {
        const updatedData = data.filter(
          (baseItem) => baseItem.releaseId !== itemToDelete.releaseId
        );
        setData(updatedData);
        updateCounters(updatedData);
        addToast("Base excluida com sucesso!");
        handleCloseDeleteModal();
        return;
      }

      let message = "Erro ao excluir base";
      try {
        const errorPayload = await response.json();
        message = errorPayload.message || errorPayload.error || message;
      } catch (parseError) {
        // payload de erro pode vir sem JSON
      }

      addToast(message);
    } catch (error) {
      addToast(`Erro ao excluir base: ${error}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredData = data.filter((item) => {
    const baseNameMatch =
      filterBaseName === "all" ? true : item.baseName === filterBaseName;

    if (filterStatus === "released") {
      return baseNameMatch && isBaseReleased(item.isReleased);
    }

    if (filterStatus === "blocked") {
      return baseNameMatch && !isBaseReleased(item.isReleased);
    }

    return baseNameMatch;
  });

  const uniqueBaseNames = Array.from(
    new Set(data.map((item) => item.baseName).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, "pt-BR"));

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <PageTitle text="bases" />
          </Col>
        </Row>
      </Container>
      <Container fluid>
        <Row>
          <Col md={3}>
            <Card>
              <Card.Header className="bg-secondary text-white">
                Bases
              </Card.Header>
              <Card.Body>
                <h1 className="app-text-orange text-center">
                  <i className="bi bi-database me-2"></i>
                  {totalBases}
                </h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">
                total de bases
              </Card.Footer>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Header className="bg-secondary text-white">
                Releases
              </Card.Header>
              <Card.Body>
                <h1 className="app-text-orange text-center">
                  <i className="bi bi-database me-2"></i>
                  {totalReleases}
                </h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">
                total de releases
              </Card.Footer>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Header className="bg-secondary text-white">
                Liberadas
              </Card.Header>
              <Card.Body>
                <h1 className="text-success text-center">
                  <i className="bi bi-check-circle me-2"></i>
                  {totalReleased}
                </h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">
                total de bases liberadas
              </Card.Footer>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Header className="bg-secondary text-white">
                Bloqueadas
              </Card.Header>
              <Card.Body>
                <h1 className="text-danger text-center">
                  <i className="bi bi-slash-circle me-2"></i>
                  {totalBlocked}
                </h1>
              </Card.Body>
              <Card.Footer className="text-center text-muted">
                total de bases bloqueadas
              </Card.Footer>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <Button
              variant="primary"
              size="sm"
              className="float-start"
              onClick={refreshBases}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Atualizar"
              )}
            </Button>
          </Col>
        </Row>

        <Row className="mt-3 g-2 align-items-end">
          <Col md={4}>
            <Form.Group controlId="filterBaseName">
              <Form.Label>Nome da base</Form.Label>
              <Form.Select
                size="sm"
                value={filterBaseName}
                onChange={(event) => setFilterBaseName(event.target.value)}
              >
                <option value="all">Todas</option>
                {uniqueBaseNames.map((baseName) => (
                  <option key={baseName} value={baseName}>
                    {baseName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="filterStatus">
              <Form.Label>Status</Form.Label>
              <Form.Select
                size="sm"
                value={filterStatus}
                onChange={(event) => setFilterStatus(event.target.value)}
              >
                <option value="all">Todos</option>
                <option value="released">Liberada</option>
                <option value="blocked">Bloqueada</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2}>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setFilterBaseName("all");
                setFilterStatus("all");
              }}
            >
              Limpar filtros
            </Button>
          </Col>
        </Row>

        <Row>
          <Col className="mt-2">
            <Table size="sm" className="small-font-table" striped hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Importação</th>
                  <th>Base</th>
                  <th>Ano</th>
                  <th>Mês</th>
                  <th>Liberada</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {!filteredData || filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center">
                      Nenhum dado encontrado
                    </td>
                  </tr>
                ) : null}

                {filteredData.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.importDate}</td>
                      <td>
                        {" "}
                        {isBaseReleased(item.isReleased) ? (
                          item.baseName
                        ) : (
                          <span className="text-danger">{item.baseName}</span>
                        )}
                      </td>
                      <td>{item.releaseYear}</td>
                      <td>{item.releaseMonth}</td>
                      <td>
                        {" "}
                        {isBaseReleased(item.isReleased) ? (
                          "Sim"
                        ) : (
                          <span className="text-danger">Não</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary-inverted"
                          onClick={() => handleDetails(item)}
                        >
                          Detalhes
                        </button>
                        &nbsp;
                        <button
                          className="btn btn-sm btn-primary w-25"
                          onClick={() => handleRelease(item)}
                        >
                          {isBaseReleased(item.isReleased) ? "Bloquear" : "Liberar"}
                        </button>
                        {!isBaseReleased(item.isReleased) ? (
                          <>
                            &nbsp;
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleOpenDeleteModal(item)}
                            >
                              Excluir
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container>

      {/* Modal/Popup de Detalhes */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        dialogClassName="settings-bases-details-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fs-6 fw-semibold text-uppercase text-muted">
            Detalhes da Base
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center p-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
              <p className="mt-2">Carregando detalhes...</p>
            </div>
          ) : itemDetails ? (
            <>
              <Card className="details-card mb-3 border-0 shadow-sm">
                <Card.Body className="py-3 px-3">
                  <div className="details-grid">
                    <div><span>Importação</span><strong>{selectedItem?.importDate}</strong></div>
                    <div><span>Release ID</span><strong>{selectedItem?.releaseId}</strong></div>
                    <div><span>Base</span><strong>{selectedItem?.baseName}</strong></div>
                    <div><span>Status</span><strong>{selectedItem?.isReleased ? "Liberada" : "Bloqueada"}</strong></div>
                    <div><span>Ano</span><strong>{selectedItem?.releaseYear}</strong></div>
                    <div><span>Mês</span><strong>{selectedItem?.releaseMonth}</strong></div>
                  </div>
                </Card.Body>
              </Card>

              <Card className="details-card border-0 shadow-sm">
                <Card.Header className="bg-secondary text-white py-2">
                  Dados Consolidados
                </Card.Header>
                <Card.Body className="py-3 px-3">
                  <div className="details-grid">
                    <div><span>Grupos de Custo</span><strong>{itemDetails.totalCostGroups || 0}</strong></div>
                    <div><span>Leis Sociais</span><strong>{itemDetails.totalTaxes || 0}</strong></div>
                    <div><span>Insumos</span><strong>{itemDetails.totalElementsI || 0}</strong></div>
                    <div><span>Equipamentos</span><strong>{itemDetails.totalElementsE || 0}</strong></div>
                    <div><span>Serviços</span><strong>{itemDetails.totalElementsS || 0}</strong></div>
                    <div><span>Recursos</span><strong>{itemDetails.totalResources || 0}</strong></div>
                    <div><span>FIC</span><strong>{itemDetails.totalFic || 0}</strong></div>
                  </div>
                </Card.Body>
              </Card>
            </>
          ) : (
            <div className="text-center p-4">
              <p>Nenhum detalhe encontrado.</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        centered
        dialogClassName="settings-bases-delete-modal"
      >
          <Modal.Header closeButton className="bg-secondary text-white py-2">
            <Modal.Title className="fs-6 fw-semibold text-uppercase">
              Confirmar exclusao da base
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-2 small">
              Para confirmar a exclusão da base <strong>{itemToDelete?.baseName}</strong>,
              digite <strong>excluir</strong> no campo abaixo.
            </p>
            <Form.Control
              type="text"
              size="sm"
              placeholder="Digite: excluir"
              value={deleteText}
              onChange={(event) => setDeleteText(event.target.value)}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" size="sm" onClick={handleCloseDeleteModal}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={handleDeleteBase}
              disabled={deleteText.trim().toLowerCase() !== "excluir" || isDeleting}
            >
              {isDeleting ? "Excluindo..." : "Confirmar"}
            </Button>
          </Modal.Footer>
      </Modal>
    </>
  );
};

export default SettingsBases;
